import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

// Enable translations
import { TranslateModule } from '@ngx-translate/core';

// Pipes
import { PipesModule } from '@app/shared/pipes';
import { CountryTransformPipe } from '@src/app/shared/pipes/country.pipe';

// Directives
import { DirectivesModule } from '@src/app/shared/directives';

// Forms
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// Table filter settings
import { TableFilterComponent } from '@src/app/shared/components/table-filter/table-filter.component';

// PrimeNG form components
import { SelectButtonModule } from 'primeng/selectbutton';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { InputNumberModule } from 'primeng/inputnumber';
import { ListboxModule } from 'primeng/listbox';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputOtpModule } from 'primeng/inputotp';
import { MultiSelectModule } from 'primeng/multiselect';
import { PasswordModule } from 'primeng/password';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ColorPickerModule } from 'primeng/colorpicker';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

// PrimeNG button components
import { ButtonModule } from 'primeng/button';
import { SplitButtonModule } from 'primeng/splitbutton';
import { SpeedDialModule } from 'primeng/speeddial';

// PrimeNG data components
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { TimelineModule } from 'primeng/timeline';
import { TreeTableModule } from 'primeng/treetable';

// PrimeNG panel components
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { FieldsetModule } from 'primeng/fieldset';
import { PanelModule } from 'primeng/panel';
import { StepperModule } from 'primeng/stepper';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { TabsModule } from 'primeng/tabs';
import { ToolbarModule } from 'primeng/toolbar';

// PrimeNG overlay components
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { DialogModule } from 'primeng/dialog';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';

// PrimeNG file components
import { FileUploadModule } from 'primeng/fileupload';

// PrimeNG breadcrumb components
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { PanelMenuModule } from 'primeng/panelmenu';
import { StepsModule } from 'primeng/steps';

// PrimeNG misc components
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { BadgeModule } from 'primeng/badge';
import { ChipModule } from 'primeng/chip';
import { ProgressBarModule } from 'primeng/progressbar';

// PrimeNG customized components
import { SelectorModule } from '@src/app/shared/components/selector/selector.module';

// QR generator
import { QRCodeComponent } from 'angularx-qrcode';

// App components
import { HeaderComponent } from '@src/app/core/components/base-layout/header/header.component';
import { SidebarComponent } from '@src/app/core/components/base-layout/sidebar/sidebar.component';
import { FooterComponent } from '@src/app/core/components/base-layout/footer/footer.component';
import { YourAccountComponent } from '@app/features/your-account/your-account.component';
import { SystemSettingsComponent } from '@app/features/system-settings/system-settings.component';
import { PrivacyPolicyComponent } from '@app/features/privacy-policy/privacy-policy.component';
import { TermsAndConditionsComponent } from '@app/features/terms-and-conditions/terms-and-conditions.component';
import { CompanyFormComponent } from '@src/app/features/system-settings/tabs/company-settings/company-form/company-form.component';
import { CompanySettingsComponent } from '@app/features/system-settings/tabs/company-settings/company-settings.component';
import { BrandsSettingsComponent } from '@app/features/system-settings/tabs/brands-settings/brands-settings.component';
import { PermissionsSettingsComponent } from '@app/features/system-settings/tabs/permissions-settings/permissions-settings.component';
import { RolesSettingsComponent } from '@app/features/system-settings/tabs/roles-settings/roles-settings.component';
import { SerializationSettingsComponent } from '@app/features/system-settings/tabs/serialization-settings/serialization-settings.component';
import { TranslationsSettingsComponent } from '@app/features/system-settings/tabs/translations-settings/translations-settings.component';
import { ThemeSettingsComponent } from '@src/app/features/system-settings/tabs/branding-settings/tabs/theme-settings/theme-settings.component';
import { DigitalCardSettingsComponent } from '@src/app/features/system-settings/tabs/digital-card-settings/digital-card-settings.component';
import { ToastModule } from 'primeng/toast';
import { ExportsComponent } from '@src/app/features/exports/exports.component';
import { DashboardComponent } from '@app/features/dashboard/dashboard.component';
import { ListBoxSelectionComponent } from '@src/app/shared/components/listbox-selection/listbox-selection.component';
import { AccordionModule } from '@src/app/shared/components/accordion/accordion.module';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { DialogExportComponent } from '@src/app/shared/components/dialog-export/dialog-export.component';
import { DialogInventoryPendingComponent } from '@src/app/shared/components/dialog-pending-inventory/dialog-pending-inventory.component';
import { TooltipInfoComponent } from '@src/app/shared/components/tooltip-info/tooltip-info.component';
import { TextboxCameraComponent } from '@src/app/shared/components/textbox-camera/textbox-camera.component';
import { SingleScannerComponent } from '@src/app/shared/components/single-scanner/single-scanner.component';
import { MultiScannerComponent } from '@src/app/shared/components/multi-scanner/multi-scanner.component';
import { NoticeComponent } from '@src/app/shared/components/notice/notice.component';

import { PatternsSettingsComponent } from '@src/app/features/system-settings/tabs/patterns-settings/patterns-settings.component';
import { ExportColumnsSettingsComponent } from '@src/app/features/system-settings/tabs/export-columns-settings/export-columns-settings.component';
import { FeaturesManagementComponent } from '@src/app/features/system-settings/tabs/features-management/features-management.component';
import { TablesColumnsSettingsComponent } from '@src/app/features/system-settings/tabs/tables-columns-settings/tables-columns-settings.component';
import { SpecificationsSettingsComponent } from '@src/app/features/system-settings/tabs/specifications-feature-settings/specifications-feature-settings.component';
import { MachineClientSettingsComponent } from '@src/app/features/system-settings/tabs/machine-client-settings/machine-client-settings.component';
import { IntegrationsSettingsComponent } from '@src/app/features/system-settings/tabs/integrations-settings/integrations-settings.component';
import { BrandingSettingsComponent } from '@src/app/features/system-settings/tabs/branding-settings/branding-settings.component';
import { UIKitComponent } from './ui-kit.component';
import { CustomDropdownModule } from '@src/app/shared/components/dropdown/custom-dropdown.module';
import { FiltersPanelModule } from '@src/app/shared/components/filters-panel/filters-panel.module';
import { CustomPaginatorModule } from '@src/app/shared/components/paginator/custom-paginator.module';
import { CustomSortPanelModule } from '@src/app/shared/components/sort-panel/custom-sort-panel.module';
import { DialogSurveySettingsComponent } from '@src/app/shared/components/dialog-survey-settings/dialog-survey-settings.component';
import { WelcomeComponent } from '@src/app/features/welcome/welcome.component';
import { NoticesSettingsComponent } from '@src/app/features/system-settings/tabs/notices-settings/notices-settings.component';

// WYSIWYG editor
import { QuillModule } from 'ngx-quill';

@NgModule({
  declarations: [
    UIKitComponent,
    CountryTransformPipe,
    ExportsComponent,
    FooterComponent,
    HeaderComponent,
    CompanyFormComponent,
    CompanySettingsComponent,
    BrandsSettingsComponent,
    PermissionsSettingsComponent,
    PrivacyPolicyComponent,
    RolesSettingsComponent,
    SpecificationsSettingsComponent,
    PatternsSettingsComponent,
    MachineClientSettingsComponent,
    IntegrationsSettingsComponent,
    BrandingSettingsComponent,
    TablesColumnsSettingsComponent,
    FeaturesManagementComponent,
    ExportColumnsSettingsComponent,
    SerializationSettingsComponent,
    ThemeSettingsComponent,
    DigitalCardSettingsComponent,
    SidebarComponent,
    ListBoxSelectionComponent,
    DialogReasonComponent,
    DialogExportComponent,
    SystemSettingsComponent,
    TableFilterComponent,
    TermsAndConditionsComponent,
    TranslationsSettingsComponent,
    YourAccountComponent,
    DashboardComponent,
    DialogInventoryPendingComponent,
    TextboxCameraComponent,
    TooltipInfoComponent,
    SingleScannerComponent,
    MultiScannerComponent,
    DialogSurveySettingsComponent,
    WelcomeComponent,
    NoticeComponent,
    NoticesSettingsComponent,
  ],
  imports: [
    RouterModule,
    AvatarGroupModule,
    AvatarModule,
    BadgeModule,
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ChipModule,
    DatePickerModule,
    SelectModule,
    ToggleSwitchModule,
    CommonModule,
    ConfirmDialogModule,
    ConfirmPopupModule,
    DialogModule,
    DirectivesModule,
    DividerModule,
    DynamicDialogModule,
    FieldsetModule,
    FileUploadModule,
    FloatLabelModule,
    FormsModule,
    IconFieldModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputIconModule,
    InputNumberModule,
    InputOtpModule,
    TextareaModule,
    InputTextModule,
    ListboxModule,
    MultiSelectModule,
    PaginatorModule,
    PanelMenuModule,
    PanelModule,
    PasswordModule,
    PipesModule,
    ProgressBarModule,
    RadioButtonModule,
    ReactiveFormsModule,
    ScrollPanelModule,
    SelectButtonModule,
    SelectorModule,
    SpeedDialModule,
    SplitButtonModule,
    StepperModule,
    StepsModule,
    TableModule,
    TimelineModule,
    ToastModule,
    ToggleButtonModule,
    ColorPickerModule,
    ToolbarModule,
    TooltipModule,
    TranslateModule,
    TreeTableModule,
    QRCodeComponent,
    AccordionModule,
    CustomDropdownModule,
    FiltersPanelModule,
    CustomPaginatorModule,
    CustomSortPanelModule,
    PopoverModule,
    TabsModule,
    QuillModule
  ],
  exports: [
    AvatarGroupModule,
    AvatarModule,
    BadgeModule,
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ChipModule,
    DatePickerModule,
    SelectModule,
    ToggleSwitchModule,
    CommonModule,
    ConfirmDialogModule,
    ConfirmPopupModule,
    CountryTransformPipe,
    DialogModule,
    DirectivesModule,
    DividerModule,
    DynamicDialogModule,
    ExportsComponent,
    FieldsetModule,
    FileUploadModule,
    FloatLabelModule,
    FooterComponent,
    FormsModule,
    HeaderComponent,
    IconFieldModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputIconModule,
    InputNumberModule,
    InputOtpModule,
    TextareaModule,
    InputTextModule,
    ListboxModule,
    CompanyFormComponent,
    CompanySettingsComponent,
    BrandsSettingsComponent,
    MultiSelectModule,
    PaginatorModule,
    PanelMenuModule,
    PanelModule,
    PasswordModule,
    PermissionsSettingsComponent,
    PipesModule,
    PrivacyPolicyComponent,
    ProgressBarModule,
    RadioButtonModule,
    ReactiveFormsModule,
    RolesSettingsComponent,
    SpecificationsSettingsComponent,
    PatternsSettingsComponent,
    MachineClientSettingsComponent,
    IntegrationsSettingsComponent,
    BrandingSettingsComponent,
    TablesColumnsSettingsComponent,
    FeaturesManagementComponent,
    ExportColumnsSettingsComponent,
    ScrollPanelModule,
    SelectButtonModule,
    SelectorModule,
    SerializationSettingsComponent,
    ThemeSettingsComponent,
    DigitalCardSettingsComponent,
    SidebarComponent,
    ListBoxSelectionComponent,
    DialogReasonComponent,
    DialogExportComponent,
    SpeedDialModule,
    SplitButtonModule,
    StepperModule,
    StepsModule,
    SystemSettingsComponent,
    TableFilterComponent,
    TableModule,
    TermsAndConditionsComponent,
    TimelineModule,
    ToastModule,
    ToggleButtonModule,
    ColorPickerModule,
    ToolbarModule,
    TooltipModule,
    TranslateModule,
    TranslationsSettingsComponent,
    TreeTableModule,
    YourAccountComponent,
    DashboardComponent,
    QRCodeComponent,
    AccordionModule,
    DialogInventoryPendingComponent,
    TextboxCameraComponent,
    TooltipInfoComponent,
    SingleScannerComponent,
    MultiScannerComponent,
    CustomDropdownModule,
    FiltersPanelModule,
    CustomPaginatorModule,
    CustomSortPanelModule,
    DialogSurveySettingsComponent,
    WelcomeComponent,
    NoticeComponent,
    TabsModule,
    NoticesSettingsComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UiKitModule { }
